import { compactSearchMatch } from "@/lib/compact-search-match"
import { getListTypeLabel } from "./document-list.labels"
import type {
  ApiDocumentSort,
  ExplorerFolder,
  ListSortColumn,
  SelectedItem,
  SortDir,
} from "./document-list.types"

/** Basename from document filePath or path (e.g. `hop-studios/foo/bar.md` -> `bar.md`). */
export function getDocumentFilename(document: { filePath?: string; path?: string }): string | null {
  const fullPath = document.filePath || document.path
  if (!fullPath) return null
  const segments = fullPath.split("/").filter(Boolean)
  return segments.length > 0 ? segments[segments.length - 1] : null
}

/** Stem used for extensionless URLs (`101-foo.md` -> `101-foo`). */
export function documentUrlStem(filePath?: string | null): string | null {
  if (!filePath) return null
  const base = filePath.split("/").filter(Boolean).pop()
  if (!base) return null
  return base.replace(/\.(md|html|csv|png|jpe?g|gif|webp|svg)$/i, "")
}

/** Folder index / hub file (`README.md` at any path depth). */
export function isReadmePath(filePath?: string | null): boolean {
  return typeof filePath === "string" && /(^|\/)README\.md$/i.test(filePath)
}

export function isReadmeDocument(document: { filePath?: string; path?: string }): boolean {
  return isReadmePath(document.filePath || document.path)
}

/** Client-side folder panel search: title, type, tags, excerpt, filename, and path. */
export function documentMatchesPanelSearch(
  doc: {
    title: string
    type: string
    tags: string[]
    excerpt?: string
    filePath?: string
    path?: string
  },
  rawQuery: string,
): boolean {
  const q = rawQuery.trim().toLowerCase()
  if (!q) return true
  const filename = getDocumentFilename(doc)
  const fullPath = doc.filePath || doc.path || ""
  return (
    compactSearchMatch(doc.title, q) ||
    compactSearchMatch(doc.type, q) ||
    doc.tags.some((t) => compactSearchMatch(t, q)) ||
    compactSearchMatch(doc.excerpt ?? "", q) ||
    compactSearchMatch(fullPath, q) ||
    (filename ? compactSearchMatch(filename, q) : false)
  )
}

/** True when the document should render as HTML (type html or .html path). */
export function isHtmlDocument(doc: { type?: string | null; filePath?: string | null; path?: string | null }): boolean {
  if (String(doc.type || "").toLowerCase() === "html") return true
  const path = String(doc.filePath || doc.path || "").toLowerCase()
  return path.endsWith(".html")
}

/** True when the document is a first-class CSV (type csv or .csv path). */
export function isCsvDocument(doc: { type?: string | null; filePath?: string | null; path?: string | null }): boolean {
  if (String(doc.type || "").toLowerCase() === "csv") return true
  const path = String(doc.filePath || doc.path || "").toLowerCase()
  return path.endsWith(".csv")
}

/** True when the document is a first-class image (type image or image extension). */
export function isImageDocument(doc: { type?: string | null; filePath?: string | null; path?: string | null }): boolean {
  if (String(doc.type || "").toLowerCase() === "image") return true
  const path = String(doc.filePath || doc.path || "").toLowerCase()
  return /\.(png|jpe?g|gif|webp|svg)$/.test(path)
}

/** Strip HTML tags and decode HTML entities for excerpt display */
export function stripHtmlTags(html: string): string {
  const temp = document.createElement("div")
  temp.innerHTML = html
  return temp.textContent || temp.innerText || ""
}

/**
 * Shared list-view column template for header + every row (folders + docs).
 * Breakpoints hide Type (<sm) and Lines, Size, and Author (<md) via `hidden` on those cells;
 * `display: none` drops them from the grid so tracks stay aligned.
 */
/** "11 files, 9 folders" plus Soul when a direct child folder is `.soul`. */
export function folderCountLine(folder: {
  fileCount?: number
  folderCount?: number
  hasSoul?: boolean
}): string | null {
  if (typeof folder.fileCount !== "number" || typeof folder.folderCount !== "number") return null
  const files = `${folder.fileCount} ${folder.fileCount === 1 ? "file" : "files"}`
  const folders = `${folder.folderCount} ${folder.folderCount === 1 ? "folder" : "folders"}`
  return folder.hasSoul ? `${files}, ${folders}, Soul` : `${files}, ${folders}`
}

/** Singular type labels, most common first. */
export function folderContentTypeLine(types: string[] | undefined): string | null {
  if (!types || types.length === 0) return null
  const labels = types.slice(0, 3).map((type) => getListTypeLabel(type)).filter(Boolean)
  return labels.length > 0 ? labels.join(", ") : null
}

export function folderTagLine(tags: string[] | undefined): string | null {
  if (!tags || tags.length === 0) return null
  return tags.slice(0, 3).map((tag) => `#${tag}`).join(" ")
}

/** One short token for the list Author column: a tag, else a type, else Soul. */
export function folderColumnHint(folder: {
  tags?: string[]
  contentTypes?: string[]
  hasSoul?: boolean
}): string {
  if (folder.tags && folder.tags.length > 0) return folder.tags[0]
  if (folder.contentTypes && folder.contentTypes.length > 0) {
    return getListTypeLabel(folder.contentTypes[0])
  }
  return folder.hasSoul ? "Soul" : ""
}

export function folderSelection(folder: ExplorerFolder): SelectedItem {
  return {
    kind: "folder",
    name: folder.name,
    path: folder.path,
    maxUpdatedAt: folder.maxUpdatedAt,
    fileCount: folder.fileCount,
    folderCount: folder.folderCount,
    hasSoul: folder.hasSoul,
    contentTypes: folder.contentTypes,
    tags: folder.tags,
  }
}

/** True when the selection is a direct child of the folder now shown in the main column. */
export function selectionLivesInFolder(
  item: SelectedItem | null,
  folderFilter: string | null | undefined,
): boolean {
  if (!item) return false
  const path = item.kind === "folder" ? item.path : (item.doc.filePath || item.doc.path || "")
  if (!path) return false
  const parts = path.replace(/\/+$/, "").split("/").filter(Boolean)
  parts.pop()
  return parts.join("/") === String(folderFilter || "").replace(/\/+$/, "")
}

export const DOCUMENT_LIST_COLS_CLASS =
  "grid w-full items-center gap-x-3 px-3 " +
  "grid-cols-[minmax(0,1fr)_6.5rem] " +
  "sm:grid-cols-[minmax(0,1fr)_5.5rem_6.5rem] " +
  "md:grid-cols-[minmax(0,1fr)_5.5rem_6.5rem_4.25rem_4.25rem_5.5rem]"

/** Approximate stored size for list and grid. Empty when there is nothing to show. */
export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes <= 0) return ""
  if (bytes < 1024) return `${Math.round(bytes)} B`
  const kb = bytes / 1024
  if (kb < 1024) return kb < 10 ? `${kb.toFixed(1)} KB` : `${Math.round(kb)} KB`
  const mb = kb / 1024
  return mb < 10 ? `${mb.toFixed(1)} MB` : `${Math.round(mb)} MB`
}

export function defaultSortDirForColumn(column: ListSortColumn): SortDir {
  return column === "modified" || column === "lines" || column === "size" ? "desc" : "asc"
}

/** Map UI column sort to GET /api/documents `sort` (type/author fall back to recent). */
export function toApiDocumentSort(column: ListSortColumn, dir: SortDir): ApiDocumentSort {
  if (column === "name") return dir === "asc" ? "title-asc" : "title-desc"
  if (column === "modified") return dir === "desc" ? "recent" : "oldest"
  return "recent"
}
